import { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Download, Mail, Loader2, CheckCircle } from 'lucide-react';
import { collegeApi, Reveal, Eyebrow, SectionTitle, Glass, Disclaimer, SectionShell, CountUp, PINK, PURPLE, GOLD } from './shared';

const GROWTH = [
  { year: '2027–28', learners: 30 }, { year: '2028–29', learners: 55 },
  { year: '2029–30', learners: 75 }, { year: '2030–31', learners: 90 },
];

export const Growth = () => (
  <SectionShell id="growth" data-testid="college-growth">
    <Reveal>
      <Eyebrow color={GOLD}>Growth &amp; Investment</Eyebrow>
      <SectionTitle>Phased growth — not a promise.</SectionTitle>
      <p className="text-white/65 max-w-3xl leading-relaxed">
        The concept is built around responsible, de-risked development in stages — proving learner demand and
        educational partnerships before any major capital commitment.
      </p>
    </Reveal>

    <div className="grid lg:grid-cols-5 gap-8 mt-12">
      <Reveal className="lg:col-span-3">
        <Glass>
          <h3 className="font-heading text-lg text-white mb-1">Illustrative student growth</h3>
          <p className="text-white/45 text-xs mb-6">Total learners per academic year — illustrative scenarios only</p>
          <div className="h-64" aria-label="Chart: illustrative learner growth from 30 in 2027-28 to 90 in 2030-31">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={GROWTH} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="collegeGrowth" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={PINK} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={PURPLE} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="year" stroke="rgba(255,255,255,0.5)" fontSize={12} />
                <YAxis stroke="rgba(255,255,255,0.5)" fontSize={12} />
                <Tooltip contentStyle={{ background: '#0b0b10', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, color: 'white' }} />
                <Area type="monotone" dataKey="learners" name="Learners" stroke={PINK} strokeWidth={3} fill="url(#collegeGrowth)" animationDuration={1600} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className="text-white/40 text-xs mt-3">Illustrative scenarios — not confirmed enrolments or guaranteed recruitment forecasts.</p>
        </Glass>
      </Reveal>

      <Reveal delay={120} className="lg:col-span-2">
        <Glass className="h-full">
          <h3 className="font-heading text-lg text-white mb-5">Financial considerations</h3>
          <div className="space-y-5">
            <div>
              <p className="font-heading text-3xl"><CountUp value={315360} prefix="£" style={{ color: GOLD }} /></p>
              <p className="text-white/55 text-xs leading-relaxed mt-1">
                Illustrative annual base-funding arithmetic at a 60-learner scenario, using the 2026–27 national rate of
                £5,256 for eligible full-time 16–17-year-old learners.
              </p>
            </div>
            <div>
              <p className="font-heading text-3xl text-white"><CountUp value={330} prefix="£" suffix="k" duration={1400} />–<CountUp value={490} prefix="£" suffix="k" duration={1800} /></p>
              <p className="text-white/55 text-xs leading-relaxed mt-1">Indicative annual operating-cost envelope.</p>
            </div>
            <p className="text-white/60 text-sm leading-relaxed">
              The project would require a financially sustainable relationship with an accredited education provider.
            </p>
          </div>
        </Glass>
      </Reveal>
    </div>
    <Reveal>
      <Disclaimer>
        All figures are preliminary planning assumptions — not guaranteed income, confirmed funding or independently
        validated financial projections.
      </Disclaimer>
    </Reveal>
  </SectionShell>
);

const ROADMAP = [
  ['Phase 1', 'Establish the Foundation', 'Secure suitable premises, confirm the commercial model, develop specialist studios and expand existing performing arts activities.', PINK],
  ['Phase 2', 'Develop Accredited Education', 'Secure the appropriate qualification approval and education arrangements, with an aspirational September 2027 launch for the post-16 programme, subject to feasibility and approvals.', PURPLE],
  ['Phase 3', 'Expand the Creative Hub', 'Develop additional specialist facilities, including aerial and acrobatic training, screen production, digital performance and further creative industry opportunities.', GOLD],
];

export const Roadmap = () => (
  <SectionShell id="roadmap" data-testid="college-roadmap">
    <Reveal>
      <Eyebrow>Our Development Roadmap</Eyebrow>
      <SectionTitle>A phased path to a sustainable creative institution.</SectionTitle>
      <p className="text-white/65 max-w-3xl leading-relaxed">
        The primary focus remains professional performing arts training — developed responsibly, in phases.
      </p>
    </Reveal>
    <div className="mt-12 relative">
      <div className="absolute left-5 top-2 bottom-2 w-px bg-gradient-to-b from-[#FF007F] via-[#7000FF] to-[#FFD700] sm:left-1/2" aria-hidden="true" />
      <div className="space-y-12">
        {ROADMAP.map(([phase, title, body, color], i) => (
          <Reveal key={phase} delay={i * 100}>
            <div className={`relative flex gap-6 sm:gap-0 ${i % 2 ? 'sm:flex-row-reverse' : ''}`}>
              <div className="college-dot-pulse absolute left-5 sm:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full border-2 bg-[#050505] mt-1.5" style={{ borderColor: color }} aria-hidden="true" />
              <div className={`pl-14 sm:pl-0 sm:w-1/2 ${i % 2 ? 'sm:pl-12' : 'sm:pr-12 sm:text-right'}`}>
                <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color }}>{phase}</p>
                <p className="font-heading text-xl text-white mt-1">{title}</p>
                <p className="text-white/60 text-sm leading-relaxed mt-2">{body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
    <Reveal>
      <Disclaimer>All phases are indicative and depend on feasibility, funding, regulatory requirements and partner agreement.</Disclaimer>
    </Reveal>
  </SectionShell>
);

const IMPACT = [
  ['Young People', 'Skills, confidence, qualifications and progression.'],
  ['Emerging Artists', 'Opportunities for new writing, film and original production.'],
  ['Creative Businesses', 'Space for collaboration, training and project development.'],
  ['Local Communities', 'Performances, workshops, participation and cultural activities.'],
  ['The Regional Economy', 'Creative talent development and potential employment pathways.'],
];

export const Impact = () => (
  <section id="impact" data-college-section="impact" data-testid="college-impact" className="relative py-20 sm:py-28"
    style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(112,0,255,0.15), transparent 60%)' }}>
    <div className="px-6 sm:px-10 lg:px-24 max-w-[1400px] mx-auto text-center">
      <Reveal>
        <Eyebrow color={PINK}>Cultural &amp; Community Impact</Eyebrow>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white leading-tight max-w-4xl mx-auto">
          More Than a College.<br /><span style={{ color: GOLD }}>A Home for Creative Possibility.</span>
        </h2>
        <p className="text-white/65 max-w-3xl mx-auto leading-relaxed mt-6">
          A thriving, year-round creative destination for Milton Keynes — serving education by day and the city&rsquo;s
          artists, audiences and creative businesses throughout the week.
        </p>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-12 text-left">
        {IMPACT.map(([t, b], i) => (
          <Reveal key={t} delay={i * 100}>
            <Glass className="h-full !p-5">
              <h3 className="text-white font-semibold text-sm mb-2">{t}</h3>
              <p className="text-white/55 text-xs leading-relaxed">{b}</p>
            </Glass>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const AREAS = [
  ['Educational Partnerships', 'Explore accredited delivery arrangements, curriculum development, student progression and shared expertise.', PINK],
  ['Industry Partnerships', 'Provide professional insight, workshops, creative briefs, mentoring and potential progression opportunities.', PURPLE],
  ['Property & Development', 'Explore suitable existing buildings, meanwhile-use opportunities, regeneration sites and phased facilities.', GOLD],
  ['Funding & Investment', 'Discuss feasibility support, eligible funding opportunities, sponsorship and sustainable commercial models.', '#3ED598'],
];

export const Partnerships = () => {
  const [form, setForm] = useState({ name: '', organisation: '', email: '', area: '', message: '', website: '' });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    try {
      await collegeApi.post('/college/enquiry', form);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong — please try again or email us directly.');
    } finally { setBusy(false); }
  };

  const downloadDeck = async () => {
    setDownloading(true);
    try {
      const r = await collegeApi.get('/college/deck', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'UPA College Investment and Partnership Deck.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError('Download failed — please refresh and try again.'); }
    finally { setDownloading(false); }
  };

  const field = 'w-full px-4 py-3 rounded-xl bg-black/50 border border-white/15 text-white placeholder-white/30 focus:outline-none focus:border-[#FF007F] transition-colors text-sm';

  return (
    <SectionShell id="partnerships" data-testid="college-partnerships">
      <Reveal>
        <Eyebrow color={GOLD}>Partnership Opportunities</Eyebrow>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white leading-tight mb-6 max-w-4xl">
          HELP US BUILD THE FUTURE OF <span style={{ color: PINK }}>CREATIVE EDUCATION</span>
        </h2>
        <p className="text-white/65 max-w-3xl leading-relaxed">
          UPA is seeking conversations with organisations interested in helping develop and test the concept —
          across education, industry, property and funding.
        </p>
      </Reveal>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
        {AREAS.map(([t, b, c], i) => (
          <Reveal key={t} delay={i * 110}>
            <Glass className="h-full !p-5 hover:border-white/25 transition-colors">
              <div className="w-8 h-1 rounded-full mb-4" style={{ background: c }} />
              <h3 className="text-white font-semibold text-sm mb-2">{t}</h3>
              <p className="text-white/55 text-xs leading-relaxed">{b}</p>
            </Glass>
          </Reveal>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mt-14 items-start">
        <Reveal>
          <Glass data-testid="college-enquiry-form">
            <h3 className="font-heading text-xl text-white mb-5">Arrange a Partnership Discussion</h3>
            {sent ? (
              <div className="text-center py-10" data-testid="college-enquiry-success">
                <CheckCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#3ED598' }} />
                <p className="text-white font-semibold">Thank you — your enquiry has been received.</p>
                <p className="text-white/55 text-sm mt-2">The UPA team will be in touch shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <input data-testid="college-enquiry-name" required aria-label="Your name" className={field} placeholder="Name *"
                  value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                <input data-testid="college-enquiry-org" aria-label="Organisation" className={field} placeholder="Organisation"
                  value={form.organisation} onChange={e => setForm(f => ({ ...f, organisation: e.target.value }))} />
                <input data-testid="college-enquiry-email" required type="email" aria-label="Email address" className={field} placeholder="Email *"
                  value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                <select data-testid="college-enquiry-area" aria-label="Area of interest" className={field}
                  value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))}>
                  <option value="">Area of interest…</option>
                  {AREAS.map(([t]) => <option key={t} value={t}>{t}</option>)}
                  <option value="Other">Other</option>
                </select>
                <textarea data-testid="college-enquiry-message" rows={4} aria-label="Message" className={field} placeholder="Message"
                  value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
                <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden"
                  value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} />
                {error && <p className="text-[#FF007F] text-sm" role="alert">{error}</p>}
                <button type="submit" disabled={busy} data-testid="college-enquiry-submit"
                  className="w-full py-4 rounded-xl font-bold text-white transition-transform hover:scale-[1.02] disabled:opacity-60"
                  style={{ background: 'linear-gradient(90deg, #FF007F, #7000FF)' }}>
                  {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Send Enquiry'}
                </button>
                <p className="text-white/40 text-xs leading-relaxed">
                  Your details are used only to respond to your enquiry, in line with our privacy policy. Or email us
                  directly at <a href="mailto:theteam@upaschool.com" className="text-[#FF007F] hover:underline">theteam@upaschool.com</a>.
                </p>
              </form>
            )}
          </Glass>
        </Reveal>

        <Reveal delay={120}>
          <Glass className="border-[#FFD700]/30">
            <h3 className="font-heading text-xl text-white mb-3">Explore the Full Proposal</h3>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Download the latest UPA College Investment &amp; Partnership Deck — the master document behind this
              presentation. Access-protected and updated as the concept evolves.
            </p>
            <button onClick={downloadDeck} disabled={downloading} data-testid="college-deck-download"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-bold text-[#050505] transition-transform hover:scale-105 disabled:opacity-60"
              style={{ background: GOLD }}>
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download the Deck (PDF)
            </button>
          </Glass>
          <Glass className="mt-5">
            <p className="flex items-center gap-2 text-white/70 text-sm"><Mail className="w-4 h-4" style={{ color: PINK }} />
              Direct contact: <a href="mailto:theteam@upaschool.com" className="text-white hover:text-[#FF007F] transition-colors">theteam@upaschool.com</a>
            </p>
          </Glass>
          <p className="text-white/35 text-xs mt-8 leading-relaxed">
            This isn&rsquo;t simply another performing arts school. It&rsquo;s a vision for where the next generation of
            creative talent will learn, collaborate and begin their professional journey.
          </p>
        </Reveal>
      </div>
    </SectionShell>
  );
};
